import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { getShowDevanagari, getShowGloss, saveShowDevanagari, saveShowGloss } from '../lib/storage';

type ToastKind = 'info' | 'error' | 'success';
interface Toast {
  id: number;
  text: string;
  kind: ToastKind;
}

interface PrefsValue {
  showGloss: boolean;
  setShowGloss: (on: boolean) => void;
  showDevanagari: boolean;
  setShowDevanagari: (on: boolean) => void;
  toast: (text: string, kind?: ToastKind) => void;
}

const PrefsContext = createContext<PrefsValue | null>(null);

/** Per-device preferences plus the toast queue, shared by every page. */
export function PrefsProvider({ children }: { children: ReactNode }) {
  const [showGloss, setGloss] = useState(getShowGloss);
  const [showDevanagari, setDeva] = useState(getShowDevanagari);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const setShowGloss = useCallback((on: boolean) => {
    setGloss(on);
    saveShowGloss(on);
  }, []);

  const setShowDevanagari = useCallback((on: boolean) => {
    setDeva(on);
    saveShowDevanagari(on);
  }, []);

  const toast = useCallback((text: string, kind: ToastKind = 'info') => {
    const id = nextId.current++;
    setToasts((t) => [...t.slice(-2), { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 4500 : 2800);
  }, []);

  const value = useMemo(
    () => ({ showGloss, setShowGloss, showDevanagari, setShowDevanagari, toast }),
    [showGloss, setShowGloss, showDevanagari, setShowDevanagari, toast],
  );

  return (
    <PrefsContext.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`} role={t.kind === 'error' ? 'alert' : 'status'}>
            {t.text}
          </div>
        ))}
      </div>
    </PrefsContext.Provider>
  );
}

export function usePrefs(): PrefsValue {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error('usePrefs must be used inside PrefsProvider');
  return ctx;
}
